import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { CONSTANTS } from '../data.js';

export function useZoomPan(params: {
  scrollRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  onZoomChange?: (z: number) => void;
}) {
  const { scrollRef, contentRef, onZoomChange } = params;
  const [zoom, setZoom] = useState(1);

  const panState = useRef({ isPanning: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
  const wheelEventData = useRef<{ deltaY: number, clientX: number, clientY: number } | null>(null);
  const isZoomUpdateQueued = useRef(false);

  useEffect(() => {
    const sc = scrollRef.current;
    if (sc) {
      const targetLeft = Math.max(0, (CONSTANTS.WORKBENCH_WIDTH * zoom - sc.clientWidth) / 2);
      const targetTop = Math.max(0, (CONSTANTS.WORKBENCH_HEIGHT * zoom - sc.clientHeight) / 2);
      sc.scrollLeft = targetLeft;
      sc.scrollTop = targetTop;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // center once on mount

  useEffect(() => {
    if (onZoomChange) onZoomChange(zoom);
  }, [onZoomChange, zoom]);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const sc = scrollRef.current;
    const contentEl = contentRef.current;
    if (!sc || !contentEl) return;

    // Only allow panning when clicking on the background grid
    if (e.target !== sc && e.target !== contentEl) return;

    e.preventDefault();
    panState.current = {
      isPanning: true,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: sc.scrollLeft,
      scrollTop: sc.scrollTop,
    };
    sc.style.cursor = 'grabbing';
    sc.style.userSelect = 'none';
  }, [contentRef, scrollRef]);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!panState.current.isPanning) return;
    const sc = scrollRef.current;
    if (!sc) return;

    e.preventDefault();
    const dx = e.clientX - panState.current.startX;
    const dy = e.clientY - panState.current.startY;
    sc.scrollLeft = panState.current.scrollLeft - dx;
    sc.scrollTop = panState.current.scrollTop - dy;
  }, [scrollRef]);

  const stopPan = useCallback(() => {
    const sc = scrollRef.current;
    if (panState.current.isPanning) {
      panState.current.isPanning = false;
      if (sc) {
        sc.style.cursor = 'grab';
        sc.style.userSelect = 'auto';
      }
    }
  }, [scrollRef]);

  const onMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    stopPan();
  }, [stopPan]);

  const onMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    stopPan();
  }, [stopPan]);

  const processZoom = useCallback(() => {
    if (!wheelEventData.current) {
      isZoomUpdateQueued.current = false;
      return;
    }
    
    const sc = scrollRef.current;
    if (!sc) {
      isZoomUpdateQueued.current = false;
      return;
    }

    const { deltaY, clientX, clientY } = wheelEventData.current;
    wheelEventData.current = null; // Consume it

    const rect = sc.getBoundingClientRect();
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    setZoom(currentZoom => {
      const contentX = sc.scrollLeft + mouseX;
      const contentY = sc.scrollTop + mouseY;

      const delta = -deltaY * CONSTANTS.ZOOM_SENSITIVITY;
      const newZoom = Math.max(CONSTANTS.MIN_ZOOM, Math.min(CONSTANTS.MAX_ZOOM, currentZoom + delta));
      if (newZoom === currentZoom) return currentZoom;

      const newScrollLeft = contentX * (newZoom / currentZoom) - mouseX;
      const newScrollTop = contentY * (newZoom / currentZoom) - mouseY;

      sc.scrollLeft = newScrollLeft;
      sc.scrollTop = newScrollTop;
      
      return newZoom;
    });

    isZoomUpdateQueued.current = false;
  }, [scrollRef]);

  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    if (!wheelEventData.current) {
      wheelEventData.current = { deltaY: e.deltaY, clientX: e.clientX, clientY: e.clientY };
    } else {
      // Accumulate deltaY for smoother zoom on trackpads
      wheelEventData.current.deltaY += e.deltaY;
      wheelEventData.current.clientX = e.clientX;
      wheelEventData.current.clientY = e.clientY;
    }

    if (!isZoomUpdateQueued.current) {
      isZoomUpdateQueued.current = true;
      requestAnimationFrame(processZoom);
    }
  }, [processZoom]);

  return {
    zoom,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave,
      onWheel
    }
  };
}
